import { FileText, Image as ImageIcon } from "lucide-react";
import type { AttachmentRecord } from "@shared-core/data/api-types";

/** Read-only labelled value used across the issue tabs. */
export function ReadField({
  label,
  value,
  empty = "Not yet recorded",
}: {
  label: string;
  value?: string | null;
  empty?: string;
}) {
  const hasValue = !!value && value.trim().length > 0;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </div>
      {hasValue ? (
        <p className="text-sm whitespace-pre-wrap text-foreground">{value}</p>
      ) : (
        <p className="text-sm italic text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageType(contentType: string): boolean {
  return contentType.startsWith("image/");
}

export function IssueAttachmentList({
  attachments,
}: {
  attachments: AttachmentRecord[];
}) {
  if (attachments.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground">No attachments</p>
    );
  }
  return (
    <ul className="space-y-2">
      {attachments.map((att) => (
        <li
          key={att.id}
          className="flex items-center gap-3 rounded border border-border p-2"
        >
          {isImageType(att.content_type) ? (
            <ImageIcon className="w-4 h-4 text-muted-foreground" />
          ) : (
            <FileText className="w-4 h-4 text-muted-foreground" />
          )}
          <span className="flex-1 text-sm truncate">{att.filename}</span>
          <span className="text-xs text-muted-foreground">
            {formatBytes(att.size_bytes)}
          </span>
        </li>
      ))}
    </ul>
  );
}
